import {
	LocalePathMap, SITE_LOCALES, SiteLocale, canonicalLocationFor, hreflangAlternatesFor, symmetricPaths,
} from "./siteMeta.ts";
import {ADVERTISING_TERMS_PATH} from "../sponsor/sponsorRoutes.ts";

/**
 * Sitemap generation (ui#97, per-domain in ui#103, locale-asymmetric in ui#1003). The list of
 * indexable public routes lives here, next to the other SEO metadata, and {@link ../../vite.config.ts}
 * renders ONE SITEMAP PER SHIPPED LOCALE from it at build time — `build/sitemap.xml` (English,
 * transferify.cloud), `build/ro/sitemap.xml` (transferify.ro) and `build/de/sitemap.xml`
 * (transferify.de) today — so no sitemap can silently drift from the routes we actually ship. The
 * emitter walks `SITE_LOCALES` rather than naming files, so a market's sitemap exists the day its tree
 * does; this note still said TWO once the German market had shipped (ui#1498), which is how a doc that
 * names the files stops describing the build.
 * Each route carries a locale→path map: a route is listed on a locale's
 * origin only where that locale's tree ships it, so a Romanian-only page never appears in the English
 * sitemap as a URL that 404s. Only *indexable* public pages belong here: the private auth-gated
 * routes and the `noindex` landing shells (`/download`, `/reminder-uploads`) are deliberately
 * excluded.
 */

export interface DatedSitemapRoute {
	/**
	 * The route's per-locale paths (ui#1003): which language trees ship it, and the path it lives at in
	 * each. Most routes are {@link symmetricPaths}; a bilingual content page may map DIFFERENT slugs
	 * per locale, and a single-locale route maps only its own locale — `renderSitemapXml` then lists it
	 * on that locale's origin only.
	 */
	paths: LocalePathMap;
	/** `<changefreq>` hint. */
	changefreq: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
	/** `<priority>` (0.0–1.0) as a string, e.g. "0.8". */
	priority: string;
	/**
	 * `<lastmod>` for THIS url, as ISO `YYYY-MM-DD`, PER LOCALE (#1542, per locale in
	 * kitecorp/transferify-ui#2734).
	 *
	 * Per route rather than one date for the document, because that is the only form in which the
	 * field carries information: a sitemap that stamps the build date on all 41 urls tells a crawler
	 * that every page changed on every deploy, including deploys that changed no page at all.
	 *
	 * Per LOCALE for the same reason one step down. A page's revision is a fact about the TREE that
	 * publishes it, not about the route: transferify.de first published on 2026-09-09, so its copy of
	 * every page shipped before that date came into being then, whatever date the English page carries.
	 * One shared value told Google that every German page — the priority-1.0 home page included — had
	 * last changed two weeks before the site existed. A locale the route ships in must have an entry
	 * here; {@link renderSitemapXml} refuses a route that ships in a locale it has no date for.
	 */
	lastmod: LocaleDateMap;
}

/** A `<lastmod>` per locale, as ISO `YYYY-MM-DD` — the shape {@link DatedSitemapRoute.lastmod} takes. */
export type LocaleDateMap = Readonly<Partial<Record<SiteLocale, string>>>;

/**
 * A route in the shipping table: everything {@link DatedSitemapRoute} carries, except that it declares
 * HOW its `<lastmod>` is determined rather than the dates themselves.
 *
 * The two are separate types because the dates are not all knowable from this module. The legal pages'
 * dates live in their own copy, which is `.tsx` and therefore out of reach of anything that must stay
 * React-free (this module is imported by the Vite config, by the language switcher and by
 * node-environment tests alike), so {@link datedSitemapRoutes} resolves them from a table the build and
 * the tests read out of that copy.
 */
export interface SitemapRoute extends Omit<DatedSitemapRoute, "lastmod"> {
	revision: RouteRevision;
}

/** How one route's `<lastmod>` is determined for each locale that ships it. */
export type RouteRevision =
	/**
	 * The date this page last changed, the same in every tree — floored per locale at the date that
	 * tree first published, because no tree revised a page before it had one.
	 */
	| {readonly revisedOn: string}
	/**
	 * Read from the date this locale's own copy of that legal body states. The "Last updated" /
	 * "Ultima actualizare" / "Stand" line IS the revision claim the page makes to its reader, so a
	 * second date kept beside it can only ever drift — and had, by a month, on three live pages.
	 */
	| {readonly statedIn: LegalDocument};

/** The legal bodies whose own copy states the date they were last revised. */
export type LegalDocument =
	"terms" | "privacy" | "cookie" | "impressum" | "dataDeletion" | "advertisingTerms";

/**
 * Each dated legal body's revision date per locale, read from that locale's own copy by
 * `scripts/legalRevisions.mjs` — the build's input to {@link datedSitemapRoutes}.
 */
export type LegalRevisions = Readonly<Record<LegalDocument, LocaleDateMap>>;

/**
 * The date each market's language tree first published pages.
 *
 * The floor under every {@link RouteRevision} `revisedOn`, and the one fact this file has to state
 * rather than derive: a market's launch is not written anywhere else in the tree. Each value is the
 * commit that added that tree's catalog (`git log --diff-filter=A -- src/i18n/<locale>.ts`), which for
 * German is 2026-09-09 — the same day Search Console first saw transferify.de serve a page. English is
 * the original tree, so its floor is the repository's first commit and never binds; it is listed
 * anyway, because a total table cannot be read as "this locale was forgotten".
 *
 * One date per MARKET, not per route per locale. A per-locale date on every route would be 48 hand-kept
 * values to keep true, which is the maintenance that produced the defect this replaces.
 */
export const LOCALE_TREE_PUBLISHED: Readonly<Record<SiteLocale, string>> = {
	en: "2019-10-29",
	ro: "2026-07-10",
	de: "2026-09-09",
};

/**
 * The date the hand-kept routes below last changed.
 *
 * A single constant for the ten product pages that share it is accurate TODAY — a shared date is
 * exactly what a shared last revision means — and it is deliberately not `new Date()`: the value must
 * only move when a page does. Give a route its own `revisedOn` here when you change just that page;
 * the field exists per route precisely so one page's edit does not re-date the other nine.
 */
const STATIC_ROUTES_LAST_REVISED = {revisedOn: "2026-08-26"} as const;

/** The public, indexable routes, in descending priority. */
export const SITEMAP_ROUTES: SitemapRoute[] = [
	{paths: symmetricPaths("/"), changefreq: "weekly", priority: "1.0", revision: STATIC_ROUTES_LAST_REVISED},
	{paths: symmetricPaths("/features"), changefreq: "monthly", priority: "0.8", revision: STATIC_ROUTES_LAST_REVISED},
	{paths: symmetricPaths("/pricing"), changefreq: "monthly", priority: "0.8", revision: STATIC_ROUTES_LAST_REVISED},
	// Ad-landing / explainer pages (ui#133). Locale-less paths: every tree serves them at the same path,
	// so each locale's sitemap lists this route at that path on its OWN brand origin. It emits no `/ro`
	// or `/de` mirror <loc> — since ui#2471 a prefixed URL is the declared DUPLICATE of the brand one,
	// and this comment said otherwise until kitecorp/transferify-ui#2734.
	{paths: symmetricPaths("/p2p"), changefreq: "monthly", priority: "0.8", revision: STATIC_ROUTES_LAST_REVISED},
	{paths: symmetricPaths("/client-server"), changefreq: "monthly", priority: "0.8", revision: STATIC_ROUTES_LAST_REVISED},
	{paths: symmetricPaths("/zero-knowledge"), changefreq: "monthly", priority: "0.7", revision: STATIC_ROUTES_LAST_REVISED},
	{paths: symmetricPaths("/about"), changefreq: "monthly", priority: "0.7", revision: STATIC_ROUTES_LAST_REVISED},
	// The advertising PRESENTATION page — the buyer-facing entry point. The booking flow behind it
	// (`/advertise`) is deliberately not listed: one indexable door to the offer, and it is this one.
	{paths: symmetricPaths("/advertising"), changefreq: "monthly", priority: "0.6", revision: STATIC_ROUTES_LAST_REVISED},
	{paths: symmetricPaths("/contact"), changefreq: "monthly", priority: "0.6", revision: STATIC_ROUTES_LAST_REVISED},
	{paths: symmetricPaths("/login"), changefreq: "monthly", priority: "0.5", revision: STATIC_ROUTES_LAST_REVISED},
	// The § 312k BGB cancellation page (kitecorp/transferify-ui#2327). INDEXED on purpose, unlike the
	// booking flow above: Abs. 2 S. 4 requires it to be easily accessible, and search is how a consumer
	// who cannot find the menu finds the control. Its own date, because it shipped after the shared one.
	{paths: symmetricPaths("/cancel-contract"), changefreq: "yearly", priority: "0.4", revision: {revisedOn: "2026-09-12"}},
	// Every route below is a legal document that DATES ITSELF in its own copy, in each reader's own
	// language, so that line is what each one's `<lastmod>` is read from. Nothing here restates a date:
	// editing a legal body re-dates its sitemap entry, and `sitemap.test.ts` fails a document whose
	// entry and copy disagree.
	{paths: symmetricPaths("/terms"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "terms"}},
	// The Advertising Terms (kitecorp/transferify-ui#2602), beside the Terms because they are the
	// booking contract's own terms and a reader looking for one looks next to the other.
	{paths: symmetricPaths(ADVERTISING_TERMS_PATH), changefreq: "yearly", priority: "0.3", revision: {statedIn: "advertisingTerms"}},
	{paths: symmetricPaths("/privacy"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "privacy"}},
	{paths: symmetricPaths("/cookies"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "cookie"}},
	{paths: symmetricPaths("/data-deletion"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "dataDeletion"}},
	// Provider identification (kitecorp/transferify-ui#2291). Symmetric because all three trees serve
	// it at the same path and it identifies the same company on every brand, so each locale's copy is
	// a genuine alternate of the others.
	{paths: symmetricPaths("/impressum"), changefreq: "yearly", priority: "0.3", revision: {statedIn: "impressum"}},
];

/**
 * The `<lastmod>` one route claims in one locale.
 *
 * @throws when a legal body states no date for this locale. A market whose policies are undated must
 *   not reach a sitemap at all: the alternative is publishing some other language's date, which is the
 *   class of claim this whole mechanism exists to stop making.
 */
export function lastmodFor(revision: RouteRevision, locale: SiteLocale, legal: LegalRevisions): string {
	if ("statedIn" in revision) {
		const stated = legal[revision.statedIn][locale];
		if (stated === undefined) {
			throw new Error(`The ${locale} ${revision.statedIn} copy states no revision date, `
				+ `so its sitemap entry has no <lastmod>`);
		}
		return stated;
	}
	// ISO dates compare chronologically as strings, which is why `<lastmod>` is ISO.
	const published = LOCALE_TREE_PUBLISHED[locale];
	return revision.revisedOn >= published ? revision.revisedOn : published;
}

/**
 * The shipping routes with every locale's `<lastmod>` resolved — what {@link renderSitemapXml} renders.
 *
 * @param legal each dated legal body's revision date per locale, read from that locale's own copy
 *   (`scripts/legalRevisions.mjs`).
 * @param routes the table to date; the shipping {@link SITEMAP_ROUTES} by default, a caller-supplied
 *   table in tests.
 */
export function datedSitemapRoutes(
	legal: LegalRevisions,
	routes: readonly SitemapRoute[] = SITEMAP_ROUTES,
): DatedSitemapRoute[] {
	return routes.map(({revision, ...route}) => ({
		...route,
		lastmod: Object.fromEntries(SITE_LOCALES
			.filter((locale) => route.paths[locale] !== undefined)
			.map((locale) => [locale, lastmodFor(revision, locale, legal)])),
	}));
}

/** Absolute `<loc>` for a route path on the given origin (no trailing slash except the root). */
export function locFor(origin: string, path: string): string {
	return `${origin}${path === "/" ? "/" : path}`;
}

/**
 * Render the sitemap XML for one locale's tree.
 *
 * @param locale The language tree this sitemap belongs to — every `<loc>` is that locale's CANONICAL
 *   location for the route (`canonicalLocationFor`), and only routes whose `paths` map this locale are
 *   listed, so the .cloud sitemap lists English URLs and the .ro sitemap Romanian URLs, each restricted
 *   to the routes that tree actually ships. A locale with no brand domain lists its `/<locale>/` URLs
 *   on the main domain; where that file should then be EMITTED (its own path, or merged into the main
 *   sitemap) is a decision for the first market that ships without a domain — `vite.config.ts` writes
 *   one file per locale today, which is right for every market that has one.
 * @param routes The DATED route table to render: {@link datedSitemapRoutes} over the shipping
 *   {@link SITEMAP_ROUTES} plus the corpus, and a caller-supplied table in tests. Required rather than
 *   defaulted, because a route's dates are not knowable from this module alone — the legal pages take
 *   theirs from their own copy — and a default would have to invent them.
 *
 *   Alternates are NOT a parameter: each `<url>` derives its own
 *   `<xhtml:link rel="alternate" hreflang=…>` set from the route's locale→path map via
 *   {@link hreflangAlternatesFor} (so a paired route cross-links its counterpart's — possibly
 *   localized — slug and a single-locale route carries none), and the `xhtml` namespace is declared
 *   when any listed `<url>` carries at least one alternate.
 */
export function renderSitemapXml(
	locale: SiteLocale,
	routes: readonly DatedSitemapRoute[],
): string {
	// Routes unavailable in this locale drop out entirely (flatMap keeps the order and needs no
	// intermediate type predicate); alternates are derived per route from its own map.
	//
	// Each `<loc>` is the route's CANONICAL location (ui#2471), not "this locale's origin plus this
	// locale's path". The two are the same for every market shipped today, and deliberately identical by
	// construction rather than by coincidence: a sitemap's job is to list the URL that should be indexed,
	// so a locale served under a prefix on the main domain must be listed there and a locale with a brand
	// domain must be listed on it. That is also why the main domain's sitemap carries no `/ro/` or `/de/`
	// url — those pages canonicalise to their brand domains and are listed in THOSE sitemaps.
	const entries = routes.flatMap((route) => {
		const path = route.paths[locale];
		return path === undefined
			? []
			: [{route, ...canonicalLocationFor(locale, path), alternates: hreflangAlternatesFor(route.paths)}];
	});
	const xhtmlNs = entries.some(({alternates}) => alternates.length > 0)
		? ` xmlns:xhtml="http://www.w3.org/1999/xhtml"`
		: "";
	const urls = entries.map(({route, origin, path, alternates}) => {
		const altLinks = alternates
			.map(
				(alt) =>
					`		<xhtml:link rel="alternate" hreflang="${alt.hreflang}" href="${locFor(alt.origin, alt.path)}"/>\n`,
			)
			.join("");
		// A route this locale ships must carry this locale's date. Refused rather than emitted empty or
		// filled from another locale: `<lastmod>` is the only field here a crawler acts on, and a wrong
		// one is worse than the element being absent.
		const lastmod = route.lastmod[locale];
		if (lastmod === undefined) {
			throw new Error(`The sitemap route ${path} ships in ${locale} but carries no ${locale} <lastmod>`);
		}
		return (
			`	<url>\n` +
			`		<loc>${locFor(origin, path)}</loc>\n` +
			`		<lastmod>${lastmod}</lastmod>\n` +
			`		<changefreq>${route.changefreq}</changefreq>\n` +
			`		<priority>${route.priority}</priority>\n` +
			`${altLinks}` +
			`	</url>`
		);
	}).join("\n");
	return `<?xml version="1.0" encoding="UTF-8"?>\n` +
		`<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"${xhtmlNs}>\n` +
		`${urls}\n` +
		`</urlset>\n`;
}
